import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useMemo, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import {
  DEFAULT_LEADERBOARD_LIMIT,
  DEFAULT_TAXONOMY_SOURCE,
  LEADERBOARD_LIMIT_OPTIONS,
  LEADERBOARD_METRICS,
  type LeaderboardMetric,
  effectiveSource,
  leaderboardSources,
  metricUi,
  rankingNote,
  rowsFor,
  taxonomyPath,
  taxonomyTitle
} from './spinLeaderboard.logic.js'
import { spinDetailPath, tsLabel } from './spinMetrics.logic.js'
import { num } from './spinRefDetail.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinLeaderboard, useSpinMetrics } from './useSpinData.js'

/**
 * "Top SPINs" — which clusters in the current set are the biggest, the most varied, the most widespread
 * and the loudest.
 *
 * The column headers switch between FOUR server-computed rankings rather than re-sorting one list. That
 * is the whole design constraint of the page: the backend can only ship a top-N out of ~435k SPINs, so
 * re-sorting the top-50-by-reads by member count on the client would present an incomplete ranking as if
 * it were the top 50 by members. Switching costs nothing — all four lists arrive in one response.
 *
 * Two readings the page has to head off:
 *  1. **Members and orders are nearly the same ranking.** Members sit close to 1:1 with orders inside a
 *     SPIN, so those two tabs largely agree (the top five are the same five SPINs on production data).
 *     An unchanged table after clicking a different column reads as a broken control, so the note under
 *     the table states the measured overlap.
 *  2. **A big SPIN is not a bad SPIN.** Cluster size here is a description, not a verdict — the quality
 *     questions live on the Cohesion and Reference pages. Nothing on this page says a top-ranked SPIN is
 *     wrong.
 *
 * OPT-IN: ~7.4s (whole-set aggregation with no seed), so nothing is fetched until the user asks. The
 * cheap whole-set metrics used for the denominators do load on mount.
 */
export const SpinLeaderboardPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const [params] = useSearchParams()
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''

  const [limit, setLimit] = useState(DEFAULT_LEADERBOARD_LIMIT)
  const [metric, setMetric] = useState<LeaderboardMetric>('MEMBERS')
  const [taxSource, setTaxSource] = useState(DEFAULT_TAXONOMY_SOURCE)

  const { metrics } = useSpinMetrics(selected)
  const { leaderboard, loading, error, load } = useSpinLeaderboard(selected, limit)

  const rows = useMemo(() => rowsFor(leaderboard, metric), [leaderboard, metric])
  const note = useMemo(() => rankingNote(leaderboard, metric), [leaderboard, metric])
  // Sources come off the loaded rows, so the toggle can only ever offer sources that actually resolved.
  const sources = useMemo(() => leaderboardSources(rows), [rows])
  const source = effectiveSource(sources, taxSource)

  const snap = metrics?.snapshot

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Top SPINs" sel={sel} />

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">
          The biggest clusters in the current set
          <InfoTip tip="Ranked over the CURRENT SPIN set as of the selected run (latest-wins by assigned_at), counting only each order's latest report so a re-crunched order cannot contribute twice." />
        </h2>

        <p className="text-sm text-gray-600 max-w-4xl">
          Four different questions about the same set: how many member OTUs a cluster holds, how many{' '}
          <i>distinct sequences</i> those members actually carry, how many orders it turns up in, and how many reads it accounts for.
          Clicking a column header switches to that metric&apos;s own top-{limit} — it does not re-sort the rows on screen, because a
          top-{limit} by one metric is not a top-{limit} by another.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          {/* The ranking is also switchable from the column headers; this makes it discoverable, since a
              header that merely changes which list is shown does not look like a control on its own. */}
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              Rank by
              <InfoTip tip="Switches to that metric's own top-N, computed server-side over the whole set. All four arrive in one response, so switching is instant and never re-runs the query." />
            </span>
            <select
              className="select select-bordered select-xs"
              value={metric}
              onChange={(e) => setMetric(e.target.value as LeaderboardMetric)}
            >
              {LEADERBOARD_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              Taxonomy source
              <InfoTip tip="Which blast source fills the taxonomy column. Sources disagree — BOLD leaves domain blank where NCBI writes 'Eukaryota' — so the column always names the source it is showing. Every source ships in the same response; switching costs nothing." />
            </span>
            <select
              className="select select-bordered select-xs"
              value={source ?? ''}
              onChange={(e) => setTaxSource(e.target.value)}
              disabled={sources.length === 0}
            >
              {sources.length === 0 && <option value="">none resolved</option>}
              {sources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              Rows
              <InfoTip tip="Caps each of the four lists. The cap bounds the list only, never the aggregation — the whole set is scanned either way, so raising it costs almost nothing." />
            </span>
            <select
              className="select select-bordered select-xs"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {LEADERBOARD_LIMIT_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm btn-primary" onClick={load} disabled={loading || !selected}>
            {loading ? 'Loading… (~8s)' : leaderboard ? 'Reload (~8s)' : 'Load top SPINs (~8s)'}
          </button>
        </div>

        <p className="text-sm text-gray-500 max-w-4xl">
          Nothing is fetched until you press Load. The query takes <b>~8 seconds</b> and there is no shortcut: every current member has to
          be resolved (latest-wins by <span className="font-mono">assigned_at</span>), narrowed to its order&apos;s latest report, and joined
          to its reads before anything can be ranked. All four rankings come out of that single scan, so switching columns afterwards is
          instant. Changing the row cap or the selected run clears the result — press Load again.
        </p>
      </section>

      {error && <p className="text-error">Could not load top SPINs: {error}</p>}

      {leaderboard && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <KpiTile label="SPINs in set" value={(snap?.spinCount ?? 0).toLocaleString()} />
            <KpiTile
              label={
                <>
                  Largest cluster
                  <InfoTip tip="Member OTUs in the biggest current SPIN — the first row of the members ranking." />
                </>
              }
              value={num(rowsFor(leaderboard, 'MEMBERS')[0]?.memberCount)}
            />
            <KpiTile
              label={
                <>
                  Loudest cluster
                  <InfoTip tip="Summed reads of the highest-abundance current SPIN — the first row of the reads ranking." />
                </>
              }
              value={num(rowsFor(leaderboard, 'READS')[0]?.totalReads)}
            />
          </div>

          <Card title={`Top ${limit} by ${metricUi(metric).label.toLowerCase()}`}>
            {rows.length === 0 ? (
              <p className="text-gray-500 text-sm">No SPINs in this set.</p>
            ) : (
              <>
                {note && <p className="text-xs text-gray-500 mb-2">{note}</p>}
                <div className="overflow-x-auto max-h-[32rem]">
                  <table className="table table-xs table-pin-rows">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>SPIN</th>
                        <th>
                          Taxonomy{source ? ` · ${source}` : ''}
                          <InfoTip tip="Full resolved ladder (domain → species) for the selected blast source, empty ranks omitted. Hover a cell for the rank-by-rank breakdown — sources fill different ranks, so position in the path is not comparable between them." />
                        </th>
                        {LEADERBOARD_METRICS.map((m) => {
                          // Every metric column is clickable and says so: ▼ marks the ranking on screen,
                          // a muted ↕ marks the others. These SWITCH server-side rankings rather than
                          // re-sorting the visible rows, so there is no ascending direction to toggle.
                          const isActive = m.key === metric
                          return (
                            <th key={m.key} className="text-right">
                              <button
                                type="button"
                                aria-pressed={isActive}
                                title={isActive ? `Ranked by ${m.label.toLowerCase()}` : `Rank by ${m.label.toLowerCase()}`}
                                className={`inline-flex items-center gap-1 ${
                                  isActive ? 'font-bold' : 'link link-hover text-gray-500'
                                }`}
                                onClick={() => setMetric(m.key)}
                              >
                                {m.label}
                                <span className={isActive ? 'text-primary' : 'opacity-40'}>{isActive ? '▼' : '↕'}</span>
                              </button>
                              <InfoTip tip={m.tip} />
                            </th>
                          )
                        })}
                        <th>Amplicon</th>
                        <th>Minted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.spinId}>
                          <td className="tabular-nums text-gray-500">{i + 1}</td>
                          <td className="font-mono">
                            <Link to={spinDetailPath(r.spinId, suffix)} className="link link-primary">
                              {shortSpinId(r.spinId)}
                            </Link>
                          </td>
                          <td
                            className="max-w-md truncate"
                            title={source ? taxonomyTitle(r.taxonomyBySource?.[source], source) : undefined}
                          >
                            {source ? taxonomyPath(r.taxonomyBySource?.[source]) : '—'}
                          </td>
                          {LEADERBOARD_METRICS.map((m) => (
                            <td
                              key={m.key}
                              className={`text-right tabular-nums ${m.key === metric ? 'font-semibold' : ''}`}
                            >
                              {num(m.value(r))}
                            </td>
                          ))}
                          <td>{r.amplicon ?? '—'}</td>
                          <td className="whitespace-nowrap">{tsLabel(r.mintedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          <Card title="How to read a row">
            <div className="text-sm text-gray-600 space-y-2 max-w-4xl">
              <p>
                <b>Members vs sequences</b> is the column pair worth looking at. A cluster with many members but few distinct sequences is
                one sequence recurring across orders — common, and exactly what a SPIN is for. Many members <i>and</i> many distinct
                sequences means the cluster is genuinely absorbing variation, which is where the{' '}
                <Link to={`/admin/spin/cohesion${suffix}`} className="link link-primary">
                  cohesion
                </Link>{' '}
                view earns its keep.
              </p>
              <p>
                <b>Members and orders track each other</b> on this data — a SPIN usually picks up about one member per order — so those two
                rankings largely agree. That is a property of the data, not a bug in the control.
              </p>
              <p>
                <b>Reads is the independent one.</b> A singleton can top it outright, and several do: abundance says nothing about cluster
                size. Reads are summed over every sample of every member, counting only each order&apos;s latest report.
              </p>
              <p className="text-warning">
                <b>Size is not a verdict.</b> Nothing here says a large SPIN is a wrong SPIN. For whether a cluster is over-merged, use{' '}
                <Link to={`/admin/spin/reference${suffix}`} className="link link-primary">
                  reference concordance
                </Link>{' '}
                and cohesion; this page only answers &ldquo;which clusters carry the most weight&rdquo;.
              </p>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
